import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface TaxCertificate {
  id: number;
  customer: number;
  customer_name: string;
  certificate_file: string;
  original_filename: string;
  certificate_number?: string;
  issuing_state: string;
  issue_date?: string;
  expiration_date: string;
  status: 'ACTIVE' | 'EXPIRING_SOON' | 'EXPIRED' | 'SUPERSEDED';
  uploaded_at: string;
  uploaded_by?: number;
  uploaded_by_name?: string;
  notes?: string;
  is_expired: boolean;
  days_until_expiration: number;
  is_expiring_soon: boolean;
}

interface CustomerTaxExemptionProps {
  customerId: number;
  customerName: string;
}

const CustomerTaxExemption: React.FC<CustomerTaxExemptionProps> = ({
  customerId,
  customerName,
}) => {
  const [certificates, setCertificates] = useState<TaxCertificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    certificate_file: null as File | null,
    certificate_number: '',
    issuing_state: 'WA',
    issue_date: '',
    expiration_date: '',
    notes: '',
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadCertificates();
  }, [customerId]);

  const loadCertificates = async () => {
    try {
      setLoading(true);
      setError('');
      // Use the customer-specific endpoint with auth credentials
      const response = await axios.get(
        `${API_BASE_URL}/customers/${customerId}/tax-certificates/`,
        { withCredentials: true }
      );
      // Handle various response formats
      const data = response.data;
      let certList: TaxCertificate[] = [];

      if (Array.isArray(data)) {
        certList = data;
      } else if (data?.results && Array.isArray(data.results)) {
        certList = data.results;
      }

      setCertificates(certList);
    } catch (err: any) {
      console.error('Error loading certificates:', err);
      setError('Failed to load tax certificates');
      setCertificates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadForm({ ...uploadForm, certificate_file: e.target.files[0] });
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!uploadForm.certificate_file) {
      alert('Please select a certificate file');
      return;
    }

    if (!uploadForm.expiration_date) {
      alert('Please enter an expiration date');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('certificate_file', uploadForm.certificate_file);
      formData.append('original_filename', uploadForm.certificate_file.name);
      // Customer ID is in the URL, no need to include in form data
      formData.append('expiration_date', uploadForm.expiration_date);
      formData.append('issuing_state', uploadForm.issuing_state);

      if (uploadForm.certificate_number) {
        formData.append('certificate_number', uploadForm.certificate_number);
      }
      if (uploadForm.issue_date) {
        formData.append('issue_date', uploadForm.issue_date);
      }
      if (uploadForm.notes) {
        formData.append('notes', uploadForm.notes);
      }

      // Use the customer-specific endpoint with auth credentials
      await axios.post(`${API_BASE_URL}/customers/${customerId}/tax-certificates/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });

      // Reset form and reload certificates
      setUploadForm({
        certificate_file: null,
        certificate_number: '',
        issuing_state: 'WA',
        issue_date: '',
        expiration_date: '',
        notes: '',
      });
      setShowUploadModal(false);
      loadCertificates();
      alert('Certificate uploaded successfully!');
    } catch (err: any) {
      console.error('Upload failed:', err);
      alert('Failed to upload certificate. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (cert: TaxCertificate) => {
    try {
      // certificate_file may be an absolute Spaces URL or a relative path
      if (cert.certificate_file.startsWith('http')) {
        window.open(cert.certificate_file, '_blank');
      } else {
        const baseUrl = API_BASE_URL.replace(/\/api\/?$/, '');
        window.open(`${baseUrl}${cert.certificate_file}`, '_blank');
      }
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download certificate');
    }
  };

  const handleSupersede = async (cert: TaxCertificate) => {
    if (!window.confirm('Mark this certificate as superseded? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/tax-certificates/${cert.id}/supersede/`, {}, {
        withCredentials: true,
      });
      loadCertificates();
    } catch (err) {
      console.error('Failed to supersede certificate:', err);
      alert('Failed to update certificate status');
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'badge badge-success';
      case 'EXPIRING_SOON':
        return 'badge badge-warning';
      case 'EXPIRED':
        return 'badge badge-danger';
      case 'SUPERSEDED':
        return 'badge badge-secondary';
      default:
        return 'badge';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'Active';
      case 'EXPIRING_SOON':
        return 'Expiring Soon';
      case 'EXPIRED':
        return 'Expired';
      case 'SUPERSEDED':
        return 'Superseded';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const activeCertificate = certificates?.find(
    (cert) => cert.status === 'ACTIVE' || cert.status === 'EXPIRING_SOON'
  );

  if (loading) {
    return <div className="loading-section">Loading tax certificates...</div>;
  }

  return (
    <div className="tax-exemption-section">
      {error && <div className="alert alert-error">{error}</div>}

      {/* Current Status */}
      <div className="detail-section">
        <div className="section-header">
          <h3>Tax Exemption Status</h3>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowUploadModal(true)}
          >
            + Upload Certificate
          </button>
        </div>

        {activeCertificate ? (
          <div className="active-certificate-card">
            <div className="cert-status-row">
              <span className={getStatusBadgeClass(activeCertificate.status)}>
                {getStatusText(activeCertificate.status)}
              </span>
              <span className="cert-expiry">
                Expires: {formatDate(activeCertificate.expiration_date)}
                {activeCertificate.days_until_expiration !== undefined && (
                  <span className={activeCertificate.days_until_expiration <= 30 ? 'text-warning' : ''}>
                    {' '}({activeCertificate.days_until_expiration} days)
                  </span>
                )}
              </span>
            </div>
            <div className="cert-details">
              <div><strong>Certificate #:</strong> {activeCertificate.certificate_number || 'N/A'}</div>
              <div><strong>Issuing State:</strong> {activeCertificate.issuing_state}</div>
              <div><strong>File:</strong> {activeCertificate.original_filename}</div>
            </div>
            {activeCertificate.status === 'EXPIRING_SOON' && (
              <div className="alert alert-warning" style={{ marginTop: '10px' }}>
                This certificate is expiring soon. Please upload a new certificate.
              </div>
            )}
            <div className="cert-actions">
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => handleDownload(activeCertificate)}
              >
                Download
              </button>
            </div>
          </div>
        ) : (
          <div className="no-certificate">
            <p><strong>No Active Certificate</strong></p>
            <p className="text-muted">
              This customer does not have an active tax exemption certificate on file.
              Upload a certificate to enable tax-exempt ordering.
            </p>
          </div>
        )}
      </div>

      {/* Certificate History */}
      <div className="detail-section">
        <h3>Certificate History</h3>
        {certificates.length === 0 ? (
          <p className="text-muted">No certificates uploaded yet.</p>
        ) : (
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Certificate #</th>
                <th>State</th>
                <th>Expiration</th>
                <th>Status</th>
                <th>Uploaded</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {certificates.map((cert) => (
                <tr key={cert.id}>
                  <td>{cert.certificate_number || `#${cert.id}`}</td>
                  <td>{cert.issuing_state}</td>
                  <td>{formatDate(cert.expiration_date)}</td>
                  <td>
                    <span className={getStatusBadgeClass(cert.status)}>
                      {getStatusText(cert.status)}
                    </span>
                  </td>
                  <td>{formatDate(cert.uploaded_at)}</td>
                  <td>
                    <button
                      className="btn-link"
                      onClick={() => handleDownload(cert)}
                    >
                      Download
                    </button>
                    {(cert.status === 'ACTIVE' || cert.status === 'EXPIRING_SOON') && (
                      <button
                        className="btn-link text-warning"
                        onClick={() => handleSupersede(cert)}
                        style={{ marginLeft: '10px' }}
                      >
                        Supersede
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => !uploading && setShowUploadModal(false)}>
          <div className="modal-content upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Upload Tax Exemption Certificate</h2>
              <button
                className="modal-close"
                onClick={() => !uploading && setShowUploadModal(false)}
                disabled={uploading}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleUpload}>
              <div className="modal-body">
                <p className="text-muted" style={{ marginBottom: '15px' }}>
                  Upload a tax exemption certificate for <strong>{customerName}</strong>
                </p>

                <div className="form-group">
                  <label htmlFor="certificate_file">Certificate File *</label>
                  <input
                    type="file"
                    id="certificate_file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    required
                    disabled={uploading}
                  />
                  <small className="form-text">Accepted formats: PDF, JPG, PNG (Max 10MB)</small>
                </div>

                <div className="form-row" style={{ display: 'flex', gap: '15px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="certificate_number">Certificate Number</label>
                    <input
                      type="text"
                      id="certificate_number"
                      value={uploadForm.certificate_number}
                      onChange={(e) =>
                        setUploadForm({ ...uploadForm, certificate_number: e.target.value })
                      }
                      placeholder="Optional"
                      disabled={uploading}
                    />
                  </div>

                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="issuing_state">Issuing State *</label>
                    <select
                      id="issuing_state"
                      value={uploadForm.issuing_state}
                      onChange={(e) =>
                        setUploadForm({ ...uploadForm, issuing_state: e.target.value })
                      }
                      required
                      disabled={uploading}
                    >
                      <option value="WA">Washington</option>
                      <option value="OR">Oregon</option>
                      <option value="CA">California</option>
                      <option value="ID">Idaho</option>
                      <option value="MT">Montana</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                </div>

                <div className="form-row" style={{ display: 'flex', gap: '15px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="issue_date">Issue Date</label>
                    <input
                      type="date"
                      id="issue_date"
                      value={uploadForm.issue_date}
                      onChange={(e) =>
                        setUploadForm({ ...uploadForm, issue_date: e.target.value })
                      }
                      disabled={uploading}
                    />
                  </div>

                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="expiration_date">Expiration Date *</label>
                    <input
                      type="date"
                      id="expiration_date"
                      value={uploadForm.expiration_date}
                      onChange={(e) =>
                        setUploadForm({ ...uploadForm, expiration_date: e.target.value })
                      }
                      required
                      disabled={uploading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="notes">Notes</label>
                  <textarea
                    id="notes"
                    value={uploadForm.notes}
                    onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
                    rows={3}
                    placeholder="Any additional notes"
                    disabled={uploading}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="btn btn-secondary"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Upload Certificate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerTaxExemption;
